/**
 * Secure cookie utility functions
 * Implements OWASP best practices for cookie security
 *
 * Note: Using secure cookies instead of localStorage to reduce XSS risk.
 */

interface CookieOptions {
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
  path?: string;
  maxAge?: number;
}

const DEFAULT_OPTIONS: Required<CookieOptions> = {
  secure:
    process.env.NODE_ENV === "production" ||
    window.location.protocol === "https:",
  sameSite: "Strict",
  path: "/",
  maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
};

/**
 * Sets a secure cookie with proper encoding and security flags
 */
export function setCookie(
  name: string,
  value: string,
  options: Partial<CookieOptions> = {}
): void {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const encodedValue = encodeURIComponent(value);

  const parts = [
    `${name}=${encodedValue}`,
    `Max-Age=${opts.maxAge}`,
    `Path=${opts.path}`,
    opts.secure && "Secure",
    `SameSite=${opts.sameSite}`,
  ].filter(Boolean);

  document.cookie = parts.join("; ");
}

/**
 * Retrieves a cookie value by name
 */
export function getCookie(name: string): string | null {
  const prefix = `${name}=`;
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }

  return null;
}

/**
 * Deletes a cookie by setting it to expire immediately
 */
export function deleteCookie(name: string, path: string = "/"): void {
  const expires = "Thu, 01 Jan 1970 00:00:01 GMT";
  const isSecure = DEFAULT_OPTIONS.secure;

  // Delete with same attributes it was set with
  const parts = [
    `${name}=`,
    `Path=${path}`,
    `Expires=${expires}`,
    isSecure && "Secure",
    `SameSite=${DEFAULT_OPTIONS.sameSite}`,
  ].filter(Boolean);

  document.cookie = parts.join("; ");
}

/**
 * Clears all authentication-related cookies
 * Call this on logout to ensure clean session termination
 */
export function clearAuthCookies(): void {
  const authCookieNames = ["token", "authToken"];
  authCookieNames.forEach((name) => deleteCookie(name));
}
